/* verqo — Portal Showcase
   6 live previews of how to visualize the seller portal in the landing page.
   Hub → 5 options + 1 recommended combo.
*/
import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import {
  ArrowLeft, ArrowRight, CheckCircle2, Wallet, ScrollText, Brain, ShieldCheck,
  AlertCircle, Building2, Banknote, Truck, Play, Layers, Eye, Layout,
  MousePointerClick, Video, Sparkles, ChevronRight, Search, Bell, Settings,
  BarChart3, TrendingUp, Clock, FileText, X as XIcon, Pause,
} from "lucide-react";
import { VerqoLockup } from "../../components/VerqoLockup";

/* ────────────────────────────────────────────────────────────────────── */
/* Shared atoms                                                           */
/* ────────────────────────────────────────────────────────────────────── */
const BRAND = { fg: "#0c1612", accent: "#dcff3a" };

function MacBrowserFrame({ url, children, testid, accent = false }) {
  return (
    <div className={`rounded-xl border bg-white overflow-hidden shadow-[0_24px_64px_-24px_rgba(15,23,42,0.18)] ${accent ? "border-lime-400" : "border-slate-200"}`} data-testid={testid}>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50/70">
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-rose-300" />
          <div className="w-2.5 h-2.5 rounded-full bg-amber-300" />
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-300" />
        </div>
        <div className="ml-2 px-2.5 py-0.5 rounded-md bg-white border border-slate-200 text-[10px] text-slate-400 tracking-wide truncate flex-1 max-w-[260px]">
          {url}
        </div>
      </div>
      {children}
    </div>
  );
}

function OptionShell({ title, sub, badge, badgeColor, children }) {
  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-slate-200/60">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/preview/portal-options" className="inline-flex items-center gap-1.5 text-[12px] text-slate-500 hover:text-slate-900 transition-colors" data-testid="back-to-hub">
              <ArrowLeft className="w-3.5 h-3.5" /> All options
            </Link>
            <span className="w-px h-4 bg-slate-200" />
            <VerqoLockup />
          </div>
          <div className="flex items-center gap-3">
            {badge && (
              <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] font-bold ${badgeColor || "bg-slate-100 text-slate-600"}`}>
                {badge}
              </span>
            )}
            <span className="text-[11px] text-slate-400 hidden md:inline">preview · not shipped</span>
          </div>
        </div>
      </header>
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 pt-12 md:pt-14 pb-10">
        <div className="text-[10.5px] uppercase tracking-[0.28em] text-lime-700 mb-3">Section preview · live in the portal</div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">{title}</h1>
        <p className="mt-3 text-[14px] text-slate-600 max-w-2xl">{sub}</p>
      </div>
      <main className="max-w-[1280px] mx-auto px-6 md:px-10 pb-32">{children}</main>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* HUB · /preview/portal-options                                          */
/* ────────────────────────────────────────────────────────────────────── */
const OPTIONS = [
  { slug: "recommended", icon: Sparkles,         badge: "Recommended", color: "bg-lime-300 text-slate-900",
    name: "Combo · screenshots strip + rich dashboard",
    blurb: "The best of both. A full dashboard hero up top to anchor the section, then a horizontal strip of supporting screenshots to convey breadth." },
  { slug: "1", icon: Eye,                       badge: "Option 1",   color: "bg-slate-900 text-white",
    name: "Real screenshots strip",
    blurb: "4–5 portal screens framed in MacOS-style browser chrome, side-scrolling. Highest authenticity, lowest effort." },
  { slug: "2", icon: Layout,                    badge: "Option 2",   color: "bg-slate-900 text-white",
    name: "Full dashboard mockup",
    blurb: "One large dashboard with nav, sidebar, and 4-6 animated widgets. Looks like a real product the moment your eye lands on it." },
  { slug: "3", icon: MousePointerClick,         badge: "Option 3",   color: "bg-slate-900 text-white",
    name: "Tabbed carousel",
    blurb: "Single browser frame with tabs (Wallet · Orders · Underwriting · Insurance · Disputes). Auto-rotates, click to explore." },
  { slug: "4", icon: Layers,                    badge: "Option 4",   color: "bg-slate-900 text-white",
    name: "Scroll storytelling",
    blurb: "Sticky dashboard on the right. As you scroll, copy on the left swaps through 4 scenes and annotations highlight live changes on the screen." },
  { slug: "5", icon: Video,                     badge: "Option 5",   color: "bg-slate-900 text-white",
    name: "Loom-style video embed",
    blurb: "60-second screen recording of a real seller flow with chapter markers. Most authentic. Heaviest page weight." },
];

export function PortalOptionsHub() {
  useEffect(() => { document.title = "verqo · Portal preview options"; }, []);
  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased" data-testid="portal-options-hub">
      <header className="border-b border-slate-200/60">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/preview/landing-v2" className="inline-flex items-center gap-1.5 text-[12px] text-slate-500 hover:text-slate-900 transition-colors" data-testid="back-to-landing">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to landing
            </Link>
            <span className="w-px h-4 bg-slate-200" />
            <VerqoLockup />
          </div>
          <span className="text-[11px] text-slate-400">preview · pick one to ship</span>
        </div>
      </header>

      <section className="max-w-[1280px] mx-auto px-6 md:px-10 pt-16 md:pt-20 pb-12">
        <div className="text-[10.5px] uppercase tracking-[0.28em] text-lime-700 mb-3">Portal preview · 6 options</div>
        <h1 className="text-4xl md:text-5xl font-semibold tracking-tight max-w-3xl leading-tight">
          Pick how the landing page <span className="text-slate-400">shows off the portal.</span>
        </h1>
        <p className="mt-5 text-[15px] text-slate-600 max-w-2xl leading-relaxed">
          Each option is a fully styled live preview of how the "Live in the portal" section could look on the landing page.
          Click into any one to see it full-size. The first card is the recommended combo.
        </p>
      </section>

      <section className="max-w-[1280px] mx-auto px-6 md:px-10 pb-32">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="options-grid">
          {OPTIONS.map((o) => (
            <Link
              key={o.slug}
              to={`/preview/portal-options/${o.slug}`}
              className={`group relative rounded-2xl border bg-white hover:-translate-y-1 transition-all p-7 ${
                o.slug === "recommended" ? "border-lime-400 shadow-[0_24px_64px_-24px_rgba(132,204,22,0.35)]" : "border-slate-200 hover:border-slate-300 hover:shadow-lg"
              }`}
              data-testid={`option-card-${o.slug}`}
            >
              <div className="flex items-center justify-between mb-5">
                <div className="w-10 h-10 rounded-lg grid place-items-center" style={{ background: BRAND.fg }}>
                  <o.icon className="w-4 h-4" style={{ color: BRAND.accent }} strokeWidth={1.8} />
                </div>
                <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-bold ${o.color}`}>
                  {o.badge}
                </span>
              </div>
              <div className="text-lg font-semibold tracking-tight text-slate-900 leading-snug">{o.name}</div>
              <div className="mt-3 text-[13px] leading-relaxed text-slate-600">{o.blurb}</div>
              <div className="mt-6 inline-flex items-center gap-1.5 text-[12px] font-semibold text-lime-700 group-hover:gap-2.5 transition-all">
                Open preview <ChevronRight className="w-3.5 h-3.5" />
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Building blocks: realistic-looking portal "screens"                    */
/* ────────────────────────────────────────────────────────────────────── */
const SCREEN_W = "w-full";

function WalletScreen() {
  return (
    <div className="p-5 md:p-6 bg-white">
      <div className="flex items-baseline justify-between mb-1">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-slate-400">Wallet · today</div>
        <div className="text-[10px] text-emerald-600 inline-flex items-center gap-1 font-semibold uppercase tracking-wider">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live
        </div>
      </div>
      <div className="text-[42px] font-semibold tracking-tight tabular-nums leading-none mt-1">$284,920</div>
      <div className="text-[11.5px] text-slate-500 mt-2">+ $48,200 received today · 14 invoices</div>

      {/* Sparkline */}
      <div className="mt-4 h-14 w-full">
        <svg viewBox="0 0 300 56" className="w-full h-full" preserveAspectRatio="none">
          <defs>
            <linearGradient id="sparkFill" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#84cc16" stopOpacity="0.32" />
              <stop offset="100%" stopColor="#84cc16" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d="M0,48 L20,42 L40,38 L60,30 L80,34 L100,26 L120,28 L140,18 L160,22 L180,14 L200,18 L220,10 L240,16 L260,8 L280,12 L300,4 L300,56 L0,56 Z" fill="url(#sparkFill)" />
          <path d="M0,48 L20,42 L40,38 L60,30 L80,34 L100,26 L120,28 L140,18 L160,22 L180,14 L200,18 L220,10 L240,16 L260,8 L280,12 L300,4" fill="none" stroke="#65a30d" strokeWidth="1.5" />
        </svg>
      </div>

      <div className="mt-5 pt-5 border-t border-slate-100">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-400">Recent disbursements</div>
          <div className="text-[10.5px] text-slate-400">View all →</div>
        </div>
        <div className="space-y-2">
          {[
            { id: "INV-3392", buyer: "Lord & Taylor",    amt: 18420, t: "2m ago" },
            { id: "INV-3391", buyer: "SHEIN wholesale",  amt: 42100, t: "9m ago" },
            { id: "INV-3390", buyer: "Whatnot",          amt:  9870, t: "21m ago" },
            { id: "INV-3389", buyer: "Ashley Stewart",   amt:  6240, t: "1h ago" },
          ].map((iv) => (
            <div key={iv.id} className="flex items-center justify-between text-[12.5px] py-1">
              <div className="flex items-center gap-2.5">
                <div className="w-1.5 h-1.5 rounded-full bg-lime-500" />
                <span className="text-slate-700 font-medium">{iv.buyer}</span>
                <span className="text-slate-300 text-[10.5px]">·</span>
                <span className="text-slate-400 text-[10.5px]">{iv.id}</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-slate-400 text-[10.5px]">{iv.t}</span>
                <span className="text-slate-900 font-semibold tabular-nums">+${iv.amt.toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function OrdersScreen() {
  const rows = [
    { id: "PO-7821", buyer: "Lord & Taylor",   amt: "$18,420", status: "Funded",      score: 92 },
    { id: "PO-7820", buyer: "SHEIN wholesale", amt: "$42,100", status: "Underwriting", score: 88 },
    { id: "PO-7819", buyer: "Whatnot",         amt: "$9,870",  status: "Funded",      score: 84 },
    { id: "PO-7818", buyer: "stores.com",      amt: "$24,500", status: "Shipped",     status2: true, score: 79 },
    { id: "PO-7817", buyer: "Ashley Stewart",  amt: "$6,240",  status: "Settled",     score: 91 },
  ];
  return (
    <div className="p-5 md:p-6 bg-white">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-slate-400">Orders</div>
          <div className="text-[16px] font-semibold mt-0.5">42 active · $284k funded</div>
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-100 text-slate-600">
            <Search className="w-3 h-3" /> Search
          </div>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-lime-300 text-slate-900 font-semibold">+ New PO</div>
        </div>
      </div>
      <div className="rounded-lg border border-slate-200 overflow-hidden">
        <div className="grid grid-cols-[1fr_1.5fr_1fr_1.1fr_0.7fr] gap-2 px-3 py-2 bg-slate-50 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
          <div>PO</div><div>Buyer</div><div>Amount</div><div>Status</div><div>Score</div>
        </div>
        {rows.map((r, i) => (
          <div key={r.id} className={`grid grid-cols-[1fr_1.5fr_1fr_1.1fr_0.7fr] gap-2 px-3 py-2.5 text-[12px] items-center ${i < rows.length - 1 ? "border-b border-slate-100" : ""}`}>
            <div className="text-slate-500 tabular-nums">{r.id}</div>
            <div className="text-slate-900 font-medium truncate">{r.buyer}</div>
            <div className="text-slate-900 font-semibold tabular-nums">{r.amt}</div>
            <div>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                r.status === "Funded" ? "bg-lime-100 text-lime-700"
                : r.status === "Underwriting" ? "bg-amber-100 text-amber-700"
                : r.status === "Shipped" ? "bg-sky-100 text-sky-700"
                : "bg-slate-100 text-slate-600"
              }`}>{r.status}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-8 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-lime-500" style={{ width: `${r.score}%` }} />
              </div>
              <span className="text-[10.5px] text-slate-500 tabular-nums">{r.score}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function UnderwritingScreen() {
  return (
    <div className="p-5 md:p-6 bg-white">
      <div className="text-[10.5px] uppercase tracking-[0.22em] text-slate-400">Buyer Score · live underwriting</div>
      <div className="mt-1 flex items-baseline gap-2">
        <div className="text-[42px] font-semibold tabular-nums tracking-tight leading-none">88</div>
        <div className="text-[12px] text-slate-500">/ 100</div>
        <div className="ml-auto inline-flex items-center gap-1 text-[10.5px] text-emerald-600 font-semibold"><TrendingUp className="w-3 h-3" /> +4 this wk</div>
      </div>
      <div className="text-[13px] font-medium text-slate-700 mt-2">SHEIN wholesale · approved Net-60</div>
      <div className="mt-5 space-y-2.5">
        {[
          { label: "Payment history",   v: 94, signal: "12 / 12 on-time" },
          { label: "KYB · OFAC · AML",  v: 100, signal: "Clean · checked 2m ago" },
          { label: "Public-records",    v: 86, signal: "1 minor flag — auto-cleared" },
          { label: "Trade references",  v: 78, signal: "3 of 4 verified" },
          { label: "Capital adequacy",  v: 82, signal: "Liquidity ratio 1.4×" },
        ].map((s) => (
          <div key={s.label}>
            <div className="flex items-center justify-between text-[11.5px] mb-1">
              <span className="text-slate-700">{s.label}</span>
              <span className="text-slate-400 text-[10.5px]">{s.signal}</span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div className={`h-full rounded-full ${s.v >= 90 ? "bg-lime-500" : s.v >= 75 ? "bg-lime-400" : "bg-amber-400"}`} style={{ width: `${s.v}%` }} />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-5 pt-4 border-t border-slate-100 flex items-center gap-3">
        <div className="text-[11px] text-slate-500">AI decision · 0.8s</div>
        <span className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-lime-300 text-slate-900 text-[11px] font-semibold">
          <CheckCircle2 className="w-3 h-3" /> Approve $42,100
        </span>
      </div>
    </div>
  );
}

function InsuranceScreen() {
  return (
    <div className="p-5 md:p-6 bg-white">
      <div className="text-[10.5px] uppercase tracking-[0.22em] text-slate-400">Insurance · Allianz Trade</div>
      <div className="mt-2 flex items-baseline gap-2">
        <div className="text-[34px] font-semibold tabular-nums tracking-tight leading-none">$3.42M</div>
        <div className="text-[11px] text-slate-500">covered exposure</div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-slate-200 p-3">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Active policies</div>
          <div className="text-[20px] font-semibold tabular-nums mt-0.5">142</div>
        </div>
        <div className="rounded-lg border border-slate-200 p-3">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">Avg cover ratio</div>
          <div className="text-[20px] font-semibold tabular-nums mt-0.5">94%</div>
        </div>
      </div>
      <div className="mt-5">
        <div className="text-[10.5px] uppercase tracking-[0.18em] text-slate-400 mb-2.5">Day-60 takeover queue</div>
        <div className="space-y-2">
          {[
            { buyer: "Acme Packaging",    days: 58, amt: 8800 },
            { buyer: "Pacific Sun Apparel", days: 54, amt: 12300 },
          ].map((c) => (
            <div key={c.buyer} className="flex items-center justify-between text-[12px] px-3 py-2 rounded-lg border border-amber-200/60 bg-amber-50/40">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5 text-amber-600" />
                <span className="text-slate-700 font-medium">{c.buyer}</span>
              </div>
              <div className="text-right">
                <div className="text-slate-900 font-semibold tabular-nums">${c.amt.toLocaleString()}</div>
                <div className="text-[10px] text-amber-700">Day {c.days} · auto-claim queued</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DisputesScreen() {
  const items = [
    { buyer: "Lord & Taylor",   reason: "Short-shipment", amt: 2400, status: "Resolved",  t: "2d ago" },
    { buyer: "Whatnot",         reason: "Damaged unit",   amt:  840, status: "In review", t: "4d ago" },
    { buyer: "stores.com",      reason: "Pricing match",  amt: 1250, status: "Resolved",  t: "1w ago" },
  ];
  return (
    <div className="p-5 md:p-6 bg-white">
      <div className="flex items-baseline justify-between mb-1">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-slate-400">Disputes</div>
        <div className="text-[10.5px] text-slate-500">Median resolution · <span className="text-slate-900 font-semibold">38h</span></div>
      </div>
      <div className="text-[16px] font-semibold mt-1">2 open · 14 resolved this month</div>
      <div className="mt-5 space-y-2">
        {items.map((d, i) => (
          <div key={i} className="rounded-lg border border-slate-200 px-3 py-2.5">
            <div className="flex items-center justify-between text-[12px]">
              <div className="flex items-center gap-2">
                <AlertCircle className={`w-3.5 h-3.5 ${d.status === "Resolved" ? "text-emerald-600" : "text-amber-600"}`} />
                <span className="text-slate-900 font-medium">{d.buyer}</span>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${d.status === "Resolved" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{d.status}</span>
            </div>
            <div className="mt-1 text-[11px] text-slate-500 flex items-center justify-between">
              <span>{d.reason} · ${d.amt.toLocaleString()}</span>
              <span>{d.t}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const SCREENS = [
  { key: "wallet",       label: "Wallet",       icon: Wallet,      url: "seller.verqo.com/wallet",      C: WalletScreen },
  { key: "orders",       label: "Orders",       icon: ScrollText,  url: "seller.verqo.com/orders",      C: OrdersScreen },
  { key: "underwriting", label: "Underwriting", icon: Brain,       url: "seller.verqo.com/buyers/shein", C: UnderwritingScreen },
  { key: "insurance",    label: "Insurance",    icon: ShieldCheck, url: "seller.verqo.com/insurance",    C: InsuranceScreen },
  { key: "disputes",     label: "Disputes",     icon: AlertCircle, url: "seller.verqo.com/disputes",     C: DisputesScreen },
];

/* ────────────────────────────────────────────────────────────────────── */
/* OPTION 1 · Real screenshots strip                                       */
/* ────────────────────────────────────────────────────────────────────── */
export function PortalOption1() {
  useEffect(() => { document.title = "verqo · Option 1 — Screenshots strip"; }, []);
  return (
    <OptionShell
      title="Real screenshots strip"
      sub="Five portal screens framed in MacOS-style browser chrome, side-scrolling. Most authentic, lowest effort to ship. Each card is a real surface a seller works in every day."
      badge="Option 1"
      badgeColor="bg-slate-900 text-white"
    >
      <div className="overflow-x-auto -mx-6 md:-mx-10 px-6 md:px-10 pb-8" data-testid="screenshots-strip">
        <div className="flex gap-5" style={{ width: "max-content" }}>
          {SCREENS.map((s) => (
            <div key={s.key} className="w-[420px] flex-shrink-0" data-testid={`screen-${s.key}`}>
              <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 mb-3 flex items-center gap-2">
                <s.icon className="w-3.5 h-3.5 text-lime-700" /> {s.label}
              </div>
              <MacBrowserFrame url={s.url} testid={`browser-${s.key}`}>
                <s.C />
              </MacBrowserFrame>
            </div>
          ))}
        </div>
      </div>
      <div className="text-[12px] text-slate-500 mt-2">← scroll to see all 5 surfaces →</div>
    </OptionShell>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* OPTION 2 · Full dashboard mockup                                        */
/* ────────────────────────────────────────────────────────────────────── */
function FullDashboard({ liveTicker = true }) {
  const [balance, setBalance] = useState(284920);
  const [orders, setOrders] = useState([
    { id: "INV-3392", buyer: "Lord & Taylor",   amt: 18420, t: "2m ago" },
    { id: "INV-3391", buyer: "SHEIN wholesale", amt: 42100, t: "9m ago" },
    { id: "INV-3390", buyer: "Whatnot",         amt:  9870, t: "21m ago" },
  ]);
  const BUYERS = ["Lord & Taylor", "Ashley Stewart", "SHEIN wholesale", "stores.com", "Whatnot", "Pacific Sun Apparel"];

  useEffect(() => {
    if (!liveTicker) return;
    let nextId = 3393;
    const interval = setInterval(() => {
      const buyer = BUYERS[Math.floor(Math.random() * BUYERS.length)];
      const amt = Math.floor(Math.random() * 22000) + 2500;
      setOrders((prev) => [{ id: `INV-${nextId++}`, buyer, amt, t: "now" }, ...prev.slice(0, 2)]);
      setBalance((b) => b + amt);
    }, 4500);
    return () => clearInterval(interval);
  }, [liveTicker]);

  return (
    <MacBrowserFrame url="seller.verqo.com / dashboard" testid="full-dashboard" accent>
      <div className="grid grid-cols-[180px_1fr] min-h-[640px] bg-white">
        {/* Sidebar */}
        <aside className="bg-slate-50/70 border-r border-slate-200 p-4">
          <VerqoLockup size="sm" />
          <div className="mt-5 space-y-0.5">
            {[
              { icon: BarChart3, name: "Dashboard", active: true },
              { icon: Wallet,    name: "Wallet" },
              { icon: ScrollText, name: "Orders", badge: 42 },
              { icon: Building2, name: "Buyers" },
              { icon: Brain,     name: "Underwriting" },
              { icon: ShieldCheck, name: "Insurance" },
              { icon: AlertCircle, name: "Disputes", badge: 2 },
              { icon: Settings,  name: "Settings" },
            ].map((n) => (
              <div key={n.name} className={`flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-md text-[12px] cursor-default ${
                n.active ? "bg-white text-slate-900 font-semibold shadow-sm" : "text-slate-600"
              }`}>
                <div className="flex items-center gap-2">
                  <n.icon className="w-3.5 h-3.5" />
                  {n.name}
                </div>
                {n.badge && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-lime-300 text-slate-900 font-bold">{n.badge}</span>}
              </div>
            ))}
          </div>
          <div className="mt-6 pt-4 border-t border-slate-200">
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Plan</div>
            <div className="text-[11.5px] text-slate-700 font-semibold mt-0.5">Growth</div>
            <div className="text-[10px] text-slate-500">Unlimited volume</div>
          </div>
        </aside>

        {/* Main */}
        <main className="p-5 md:p-6">
          {/* Top bar */}
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-slate-400">Dashboard</div>
              <div className="text-[15px] font-semibold mt-0.5">Welcome back, Mia ·  <span className="text-slate-500 font-normal">Acme Apparel Co.</span></div>
            </div>
            <div className="flex items-center gap-2.5">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-200 text-[11px] text-slate-500">
                <Search className="w-3 h-3" /> Search orders
              </div>
              <div className="w-8 h-8 rounded-md border border-slate-200 grid place-items-center relative">
                <Bell className="w-3.5 h-3.5 text-slate-500" />
                <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-rose-500" />
              </div>
              <div className="w-8 h-8 rounded-full bg-lime-300 grid place-items-center text-[11px] font-bold text-slate-900">MK</div>
            </div>
          </div>

          {/* Top stat row */}
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-xl border-2 p-4" style={{ borderColor: BRAND.fg, background: BRAND.fg }}>
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: BRAND.accent }}>Wallet</div>
                <div className="text-[10px] text-white/60 inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-lime-300 animate-pulse" /> Live</div>
              </div>
              <motion.div key={balance} initial={{ scale: 1.04, color: "#dcff3a" }} animate={{ scale: 1, color: "#ffffff" }} transition={{ duration: 0.5 }} className="text-[28px] font-semibold tabular-nums tracking-tight">
                ${balance.toLocaleString()}
              </motion.div>
              <div className="text-[10.5px] text-white/55 mt-1">Available · same-day ACH</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Receivables · 30d</div>
              <div className="text-[26px] font-semibold tabular-nums tracking-tight mt-0.5">$1.24M</div>
              <div className="text-[10.5px] text-emerald-600 mt-1 inline-flex items-center gap-1"><TrendingUp className="w-3 h-3" /> +18% vs last month</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">DSO · funded</div>
              <div className="text-[26px] font-semibold tabular-nums tracking-tight mt-0.5">0 days</div>
              <div className="text-[10.5px] text-slate-500 mt-1">vs 62 days industry avg</div>
            </div>
          </div>

          {/* Sparkline + activity feed */}
          <div className="grid grid-cols-[1.4fr_1fr] gap-3 mt-3">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-baseline justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Disbursements · 30 days</div>
                  <div className="text-[13px] font-semibold mt-0.5">$482k freed up this month</div>
                </div>
                <div className="flex gap-1">
                  {["7d", "30d", "90d"].map((p, i) => (
                    <span key={p} className={`text-[10px] px-1.5 py-0.5 rounded ${i === 1 ? "bg-slate-900 text-white" : "text-slate-400"}`}>{p}</span>
                  ))}
                </div>
              </div>
              <div className="h-28 w-full">
                <svg viewBox="0 0 400 100" className="w-full h-full" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="dashFill" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#84cc16" stopOpacity="0.35" />
                      <stop offset="100%" stopColor="#84cc16" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <path d="M0,85 L25,76 L50,70 L75,72 L100,58 L125,62 L150,48 L175,52 L200,40 L225,44 L250,30 L275,36 L300,24 L325,28 L350,16 L375,20 L400,8 L400,100 L0,100 Z" fill="url(#dashFill)" />
                  <path d="M0,85 L25,76 L50,70 L75,72 L100,58 L125,62 L150,48 L175,52 L200,40 L225,44 L250,30 L275,36 L300,24 L325,28 L350,16 L375,20 L400,8" fill="none" stroke="#65a30d" strokeWidth="2" />
                  {/* Dot at end */}
                  <circle cx="400" cy="8" r="3" fill="#65a30d" />
                </svg>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-2.5">Recent disbursements</div>
              <div className="space-y-1.5">
                <AnimatePresence initial={false}>
                  {orders.map((iv) => (
                    <motion.div
                      key={iv.id}
                      initial={{ opacity: 0, y: -6, height: 0 }}
                      animate={{ opacity: 1, y: 0, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.4 }}
                      className="flex items-center justify-between text-[11.5px] py-1"
                    >
                      <div className="flex items-center gap-1.5 min-w-0">
                        <div className="w-1.5 h-1.5 rounded-full bg-lime-500 flex-shrink-0" />
                        <span className="text-slate-700 font-medium truncate">{iv.buyer}</span>
                      </div>
                      <div className="text-slate-900 font-semibold tabular-nums flex-shrink-0">+${iv.amt.toLocaleString()}</div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* AR aging table */}
          <div className="rounded-xl border border-slate-200 bg-white p-4 mt-3">
            <div className="flex items-baseline justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">AR aging</div>
              <div className="text-[10.5px] text-slate-500">Insurance kicks in at day 60</div>
            </div>
            <div className="space-y-2">
              {[
                { range: "Current",    pct: 64, amt: "$184k", color: "bg-lime-500" },
                { range: "1-30 days",  pct: 22, amt: "$62k",  color: "bg-lime-400" },
                { range: "31-60 days", pct: 11, amt: "$30k",  color: "bg-amber-400" },
                { range: "60+ days",   pct:  3, amt: "$8k",   color: "bg-rose-400" },
              ].map((r) => (
                <div key={r.range} className="grid grid-cols-[110px_1fr_72px] gap-3 items-center text-[11.5px]">
                  <div className="text-slate-600">{r.range}</div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className={`h-full ${r.color}`} style={{ width: `${r.pct}%` }} />
                  </div>
                  <div className="text-right font-semibold tabular-nums">{r.amt}</div>
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    </MacBrowserFrame>
  );
}

export function PortalOption2() {
  useEffect(() => { document.title = "verqo · Option 2 — Full dashboard"; }, []);
  return (
    <OptionShell
      title="Full dashboard mockup"
      sub="One large dashboard with nav, sidebar, and 4–6 animated widgets. Looks like a real product the moment your eye lands on it. Live ticker continues to animate, just like the seller actually sees."
      badge="Option 2"
      badgeColor="bg-slate-900 text-white"
    >
      <FullDashboard />
    </OptionShell>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* OPTION 3 · Tabbed carousel                                              */
/* ────────────────────────────────────────────────────────────────────── */
export function PortalOption3() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => { document.title = "verqo · Option 3 — Tabbed carousel"; }, []);
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setActive((a) => (a + 1) % SCREENS.length), 4200);
    return () => clearInterval(t);
  }, [paused]);
  const S = SCREENS[active];
  return (
    <OptionShell
      title="Tabbed carousel"
      sub="Single browser frame with tabs across the top. Auto-rotates every ~4 seconds — click any tab to dive in. Shows the breadth of the portal without forcing the user to scroll."
      badge="Option 3"
      badgeColor="bg-slate-900 text-white"
    >
      <MacBrowserFrame url={S.url} testid="tabbed-frame" accent>
        {/* Tab strip */}
        <div className="flex items-center gap-1 px-3 py-2 border-b border-slate-200 bg-slate-50/40 overflow-x-auto" data-testid="tab-strip">
          {SCREENS.map((s, i) => (
            <button
              key={s.key}
              onClick={() => { setActive(i); setPaused(true); }}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11.5px] transition-all flex-shrink-0 ${
                active === i ? "bg-white text-slate-900 font-semibold shadow-sm border border-slate-200" : "text-slate-500 hover:text-slate-900"
              }`}
              data-testid={`tab-${s.key}`}
            >
              <s.icon className="w-3 h-3" />
              {s.label}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2 pl-2">
            <button onClick={() => setPaused((p) => !p)} className="text-[10.5px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="carousel-toggle">
              {paused ? <><Play className="w-3 h-3 fill-current" /> Auto-play</> : <><Pause className="w-3 h-3" /> Pause</>}
            </button>
          </div>
        </div>
        {/* Active screen */}
        <div className="relative min-h-[440px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={S.key}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              <S.C />
            </motion.div>
          </AnimatePresence>
        </div>
        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1.5 py-3 border-t border-slate-100 bg-slate-50/40">
          {SCREENS.map((_, i) => (
            <button
              key={i}
              onClick={() => { setActive(i); setPaused(true); }}
              className={`h-1 rounded-full transition-all ${active === i ? "w-8 bg-lime-500" : "w-1.5 bg-slate-300"}`}
              data-testid={`dot-${i}`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      </MacBrowserFrame>
    </OptionShell>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* OPTION 4 · Scroll storytelling                                          */
/* ────────────────────────────────────────────────────────────────────── */
const SCROLL_SCENES = [
  { num: "01", title: "PO arrives", body: "Your buyer drops a Net-60 purchase order. verqo's AI parses it in under a second — vendor, SKUs, totals, terms.", screen: "orders",       hi: "po-row" },
  { num: "02", title: "AI underwrites", body: "Buyer Score updates in real time. KYB · OFAC · AML · payment history · trade refs — all weighed against our lender pool.", screen: "underwriting", hi: "score" },
  { num: "03", title: "Cash hits wallet", body: "Day-0 ACH. The full invoice (minus our flat fee) lands in your wallet the same business day. Re-buy inventory this week.", screen: "wallet",       hi: "balance" },
  { num: "04", title: "Insurance backstops it", body: "At day 60 past due, Allianz takes over. The cash you were paid? Yours. The chase? Ours.", screen: "insurance",   hi: "queue" },
];

export function PortalOption4() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end end"] });
  const [activeIdx, setActiveIdx] = useState(0);

  useEffect(() => { document.title = "verqo · Option 4 — Scroll storytelling"; }, []);
  useEffect(() => {
    return scrollYProgress.on("change", (v) => {
      const idx = Math.min(SCROLL_SCENES.length - 1, Math.floor(v * SCROLL_SCENES.length));
      setActiveIdx(idx);
    });
  }, [scrollYProgress]);

  const activeScene = SCROLL_SCENES[activeIdx];
  const ActiveScreen = SCREENS.find((s) => s.key === activeScene.screen)?.C;

  return (
    <OptionShell
      title="Scroll storytelling"
      sub="Sticky dashboard on the right. As you scroll, copy on the left swaps through 4 scenes and the screen updates to match. Stripe/Linear-style — cinematic and educational."
      badge="Option 4"
      badgeColor="bg-slate-900 text-white"
    >
      <div ref={sectionRef} className="relative" style={{ height: `${SCROLL_SCENES.length * 80}vh` }} data-testid="scroll-narrative">
        <div className="sticky top-24 grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-10 items-center">
          {/* Left — scene narrative */}
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeScene.num}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -18 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="text-[60px] md:text-[80px] leading-none font-semibold tracking-tighter text-lime-300">
                  {activeScene.num}
                </div>
                <div className="mt-3 text-3xl md:text-4xl font-semibold tracking-tight">{activeScene.title}</div>
                <p className="mt-5 text-[15px] leading-relaxed text-slate-600 max-w-md">{activeScene.body}</p>
              </motion.div>
            </AnimatePresence>

            {/* Progress dots */}
            <div className="mt-10 space-y-1.5">
              {SCROLL_SCENES.map((s, i) => (
                <div key={s.num} className="flex items-center gap-3">
                  <div className={`h-1 rounded-full transition-all ${i === activeIdx ? "w-10 bg-slate-900" : "w-4 bg-slate-200"}`} />
                  <span className={`text-[11px] tabular-nums uppercase tracking-wider ${i === activeIdx ? "text-slate-900 font-semibold" : "text-slate-400"}`}>{s.num} {s.title}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right — sticky dashboard */}
          <div className="relative">
            <MacBrowserFrame url={SCREENS.find((s) => s.key === activeScene.screen)?.url} testid="story-frame" accent>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeScene.screen}
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.35 }}
                >
                  {ActiveScreen && <ActiveScreen />}
                </motion.div>
              </AnimatePresence>
            </MacBrowserFrame>
            {/* Floating annotation badge */}
            <motion.div
              key={activeIdx}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="absolute -right-3 top-12 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.2em] font-bold shadow-lg"
              style={{ background: BRAND.fg, color: BRAND.accent }}
            >
              ← live update
            </motion.div>
          </div>
        </div>
      </div>
      <div className="text-[12px] text-slate-500 text-center mt-6">↓ keep scrolling to watch the story unfold ↓</div>
    </OptionShell>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* OPTION 5 · Loom-style video embed                                       */
/* ────────────────────────────────────────────────────────────────────── */
export function PortalOption5() {
  const [playing, setPlaying] = useState(false);
  const [chapter, setChapter] = useState(0);
  useEffect(() => { document.title = "verqo · Option 5 — Video embed"; }, []);

  const chapters = [
    { t: "0:00", title: "Welcome — meet Acme Apparel" },
    { t: "0:12", title: "PO arrives from Lord & Taylor" },
    { t: "0:28", title: "AI underwrites the buyer · 0.8s" },
    { t: "0:44", title: "Same-day ACH hits the wallet" },
    { t: "0:58", title: "Insurance backstops the invoice" },
  ];

  return (
    <OptionShell
      title="Loom-style video embed"
      sub="60-second screen recording of a real seller flow with chapter markers. Most authentic. Heaviest page weight. Best paired with a 'Watch demo' CTA throughout the site."
      badge="Option 5"
      badgeColor="bg-slate-900 text-white"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[1.7fr_1fr] gap-6">
        <div className="relative rounded-xl overflow-hidden bg-slate-900 shadow-[0_24px_64px_-24px_rgba(15,23,42,0.4)]" data-testid="video-frame">
          <div className="aspect-video relative">
            {/* Placeholder video — gradient + grid + faux UI overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
            <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
              style={{ backgroundImage: "linear-gradient(rgba(220,255,58,1) 1px, transparent 1px), linear-gradient(90deg, rgba(220,255,58,1) 1px, transparent 1px)", backgroundSize: "28px 28px" }}
            />
            <div className="absolute inset-0 opacity-25"
              style={{ background: "radial-gradient(circle at 50% 40%, rgba(220,255,58,0.4) 0%, transparent 55%)" }}
            />
            {/* Faux UI inside the video frame */}
            <div className="absolute inset-8 rounded-lg border border-white/10 bg-slate-950/40 backdrop-blur-sm overflow-hidden hidden md:block">
              <div className="px-4 py-2 border-b border-white/10 text-[10px] text-white/40 flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-rose-300" />
                <div className="w-1.5 h-1.5 rounded-full bg-amber-300" />
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-300" />
                <span className="ml-2">seller.verqo.com / wallet</span>
              </div>
              <div className="p-4">
                <div className="text-[9px] uppercase tracking-widest text-lime-300/70">Wallet · today</div>
                <div className="text-2xl font-semibold tabular-nums text-white mt-1">$284,920</div>
                <div className="text-[10px] text-white/40 mt-1">+ $48,200 received · 14 invoices</div>
              </div>
            </div>
            {/* Play button */}
            <button
              onClick={() => setPlaying(true)}
              className="absolute inset-0 grid place-items-center group"
              data-testid="play-button"
            >
              <motion.div
                animate={{ scale: [0.95, 1, 0.95] }}
                transition={{ duration: 2.4, repeat: Infinity }}
                className="w-20 h-20 rounded-full bg-lime-300 grid place-items-center shadow-[0_0_60px_-8px_rgba(220,255,58,0.9)] group-hover:scale-110 transition-transform"
              >
                <Play className="w-7 h-7 text-slate-900 fill-current ml-1" />
              </motion.div>
              {playing && (
                <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2 text-[10px] text-white/60">
                  <span className="tabular-nums">0:{(chapter * 12).toString().padStart(2, "0")}</span>
                  <div className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden">
                    <motion.div className="h-full bg-lime-300" initial={{ width: "0%" }} animate={{ width: `${((chapter + 1) / chapters.length) * 100}%` }} transition={{ duration: 0.6 }} />
                  </div>
                  <span className="tabular-nums">1:02</span>
                </div>
              )}
            </button>
            {/* Chapter title overlay */}
            <div className="absolute top-4 left-4 right-4 flex items-center gap-2 text-[11px] text-white/70">
              <span className="px-2 py-0.5 rounded-md bg-lime-300 text-slate-900 font-bold text-[9px] uppercase tracking-widest">{playing ? "Playing" : "60s demo"}</span>
              <span>{chapters[chapter].title}</span>
            </div>
          </div>
        </div>

        {/* Chapters sidebar */}
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="chapters">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-slate-400 mb-3">Chapters</div>
          <div className="space-y-1">
            {chapters.map((c, i) => (
              <button
                key={i}
                onClick={() => { setChapter(i); setPlaying(true); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                  chapter === i ? "bg-lime-50 border border-lime-300" : "border border-transparent hover:bg-slate-50"
                }`}
                data-testid={`chapter-${i}`}
              >
                <span className={`text-[10.5px] tabular-nums font-bold ${chapter === i ? "text-lime-700" : "text-slate-400"}`}>{c.t}</span>
                <span className={`text-[13px] ${chapter === i ? "text-slate-900 font-semibold" : "text-slate-600"}`}>{c.title}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </OptionShell>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* RECOMMENDED · Option 2 dashboard + Option 1 strip                       */
/* ────────────────────────────────────────────────────────────────────── */
export function PortalRecommended() {
  useEffect(() => { document.title = "verqo · Recommended — Dashboard + strip"; }, []);
  return (
    <OptionShell
      title="Combo · full dashboard + supporting strip"
      sub="The recommended combo. Full Option 2 dashboard as the centerpiece — anchors the section visually. Below it, a horizontal strip of Option 1 screenshots to convey breadth across the rest of the portal."
      badge="Recommended"
      badgeColor="bg-lime-300 text-slate-900"
    >
      {/* Centerpiece — full dashboard */}
      <div className="mb-12">
        <div className="text-[11px] uppercase tracking-[0.22em] text-lime-700 mb-3 flex items-center gap-2">
          <BarChart3 className="w-3.5 h-3.5" /> Seller dashboard · today
        </div>
        <FullDashboard />
      </div>

      {/* Supporting strip — Option 1 screenshots */}
      <div className="border-t border-slate-200 pt-12">
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 mb-2">More surfaces in the portal</div>
            <div className="text-[18px] font-semibold tracking-tight text-slate-900">Every wholesale workflow · one ledger.</div>
          </div>
          <div className="text-[11.5px] text-slate-500 hidden md:block">← scroll →</div>
        </div>
        <div className="overflow-x-auto -mx-6 md:-mx-10 px-6 md:px-10 pb-4">
          <div className="flex gap-4" style={{ width: "max-content" }}>
            {SCREENS.filter((s) => s.key !== "wallet").map((s) => (
              <div key={s.key} className="w-[340px] flex-shrink-0" data-testid={`combo-screen-${s.key}`}>
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-slate-500 mb-2.5 flex items-center gap-2">
                  <s.icon className="w-3 h-3 text-lime-700" /> {s.label}
                </div>
                <MacBrowserFrame url={s.url}>
                  <div className="scale-[0.92] origin-top-left" style={{ transform: "scale(0.92)" }}>
                    <s.C />
                  </div>
                </MacBrowserFrame>
              </div>
            ))}
          </div>
        </div>
      </div>
    </OptionShell>
  );
}
